import React, { useState, useCallback } from 'react';
import './Configuracoes.css';
import SegurancaSessoes from './SegurancaSessoes';
import { apiFetch, ApiError } from './api';
import { useRecurso } from './hooks/useRecurso';

// Abas (plural, UI) -> tipo canônico da API (singular). PRD 23 / SEF-24, US-001/002/003.
const TIPO_POR_ABA = { documentos: 'documento', emails: 'email', mensagens: 'mensagem' };

// Placeholders {NOME} presentes no conteúdo/assunto, p/ gerar os inputs do preview (US-005).
function extrairPlaceholders(template) {
  if (!template) {
    return [];
  }
  const texto = `${template.conteudo || ''} ${template.assunto || ''}`;
  const encontrados = new Set();
  const regex = /\{([A-Z_][A-Z0-9_]*)\}/g;
  let achado = regex.exec(texto);
  while (achado !== null) {
    encontrados.add(achado[1]);
    achado = regex.exec(texto);
  }
  return [...encontrados];
}

function Configuracoes() {
  const [activeTab, setActiveTab] = useState('documentos');
  const [templateEditando, setTemplateEditando] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroEditor, setErroEditor] = useState(null);
  const [excluindoId, setExcluindoId] = useState(null);
  const [erroLista, setErroLista] = useState(null);
  // Preview (PRD 23 / SEF-24, US-005): só para template já salvo (POST /{id}/preview).
  const [previewVars, setPreviewVars] = useState({});
  const [previewResultado, setPreviewResultado] = useState(null);
  const [previewCarregando, setPreviewCarregando] = useState(false);

  const tipoApi = TIPO_POR_ABA[activeTab];

  // Catálogo de variáveis disponíveis (US-006), carregado uma vez; alimenta o painel do editor.
  const buscarVariaveis = useCallback(
    () => apiFetch('/api/configuracoes/templates/variaveis-disponiveis'), []);
  const { dados: catalogoVariaveis } = useRecurso(buscarVariaveis, []);
  const variaveisDisponiveis = catalogoVariaveis?.variaveis ?? [];

  const buscar = useCallback(() => {
    if (!tipoApi) {
      return Promise.resolve({ templates: [] }); // aba Segurança não lista templates
    }
    return apiFetch(`/api/configuracoes/templates?tipo=${tipoApi}`);
  }, [tipoApi]);

  const { dados, carregando, erro, recarregar } = useRecurso(buscar, [tipoApi]);
  const templates = dados?.templates ?? [];

  const limparPreview = () => {
    setPreviewVars({});
    setPreviewResultado(null);
  };

  const handleAdicionarTemplate = () => {
    setErroEditor(null);
    limparPreview();
    setTemplateEditando({ novo: true, nome: '', assunto: '', conteudo: '' });
    setModoEdicao(true);
  };

  const handleEditarTemplate = (template) => {
    setErroEditor(null);
    limparPreview();
    setTemplateEditando({ ...template });
    setModoEdicao(true);
  };

  const handleCancelarEdicao = () => {
    setModoEdicao(false);
    setTemplateEditando(null);
    setErroEditor(null);
    limparPreview();
  };

  // Exclui (DELETE) um template do tenant (US-004); trata 409 TEMPLATE_IN_USE.
  const handleExcluirTemplate = async (template) => {
    if (excluindoId) {
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir o template "${template.nome}"?`)) {
      return;
    }
    setExcluindoId(template.id);
    setErroLista(null);
    try {
      await apiFetch(`/api/configuracoes/templates/${template.id}`, { method: 'DELETE' });
      recarregar();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'TEMPLATE_IN_USE') {
        setErroLista('Este template está em uso por uma ação e não pode ser excluído.');
      } else if (e instanceof ApiError && e.code === 'NOT_EDITABLE') {
        setErroLista('Templates padrão do sistema não podem ser excluídos.');
      } else {
        setErroLista('Não foi possível excluir o template.');
      }
    } finally {
      setExcluindoId(null);
    }
  };

  // Insere o placeholder {NOME} no fim do conteúdo em edição (painel de variáveis disponíveis, US-006).
  const inserirVariavel = (nome) => {
    setTemplateEditando((prev) => ({ ...prev, conteudo: `${prev.conteudo || ''}{${nome}}` }));
  };

  // Pré-visualiza o template salvo substituindo as variáveis informadas (US-005).
  const handlePreview = async () => {
    if (previewCarregando || !templateEditando || templateEditando.novo) {
      return;
    }
    setPreviewCarregando(true);
    setErroEditor(null);
    try {
      const resp = await apiFetch(`/api/configuracoes/templates/${templateEditando.id}/preview`, {
        method: 'POST',
        body: { variaveis: previewVars },
      });
      setPreviewResultado(resp);
    } catch (e) {
      setPreviewResultado(null);
      setErroEditor('Não foi possível gerar a pré-visualização.');
    } finally {
      setPreviewCarregando(false);
    }
  };

  // Trocar de aba durante a edição cancela o editor: evita salvar com o tipo da aba errada (review CTO #54).
  const trocarAba = (aba) => {
    if (modoEdicao) {
      handleCancelarEdicao();
    }
    setActiveTab(aba);
  };

  // Cria (POST) ou edita (PUT) via API; variáveis são deduzidas do conteúdo pelo backend.
  const handleSalvarTemplate = async () => {
    if (salvando || !templateEditando) {
      return;
    }
    const corpo = {
      tipo: tipoApi,
      nome: templateEditando.nome,
      conteudo: templateEditando.conteudo,
      ...(tipoApi === 'email' ? { assunto: templateEditando.assunto } : {}),
    };
    setSalvando(true);
    setErroEditor(null);
    try {
      if (templateEditando.novo) {
        await apiFetch('/api/configuracoes/templates', { method: 'POST', body: corpo });
      } else {
        await apiFetch(`/api/configuracoes/templates/${templateEditando.id}`, { method: 'PUT', body: corpo });
      }
      setModoEdicao(false);
      setTemplateEditando(null);
      recarregar();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'NOT_EDITABLE') {
        setErroEditor('Templates padrão do sistema não podem ser editados.');
      } else if (e instanceof ApiError && e.status === 400) {
        setErroEditor('Verifique os campos: nome e conteúdo são obrigatórios (assunto é obrigatório para e-mail).');
      } else {
        setErroEditor('Não foi possível salvar o template. Tente novamente.');
      }
    } finally {
      setSalvando(false);
    }
  };

  const renderizarListaTemplates = () => {
    if (carregando) {
      return <p className="templates-carregando">Carregando templates...</p>;
    }
    if (erro) {
      return (
        <div className="templates-erro">
          <p>Não foi possível carregar os templates.</p>
          <button className="btn btn-outline" onClick={recarregar}>Tentar novamente</button>
        </div>
      );
    }
    if (templates.length === 0) {
      return <p className="templates-vazio">Nenhum template ainda. Crie o primeiro.</p>;
    }
    return templates.map((template) => (
      <div key={template.id} className="template-item">
        <div className="template-info">
          <h3>
            {template.nome}
            {!template.editavel && <span className="template-badge-sistema">Padrão do sistema</span>}
          </h3>
          {tipoApi === 'email' && template.assunto && (
            <p className="template-assunto">Assunto: {template.assunto}</p>
          )}
          <p className="template-variaveis">
            Variáveis: {template.variaveis && template.variaveis.length ? template.variaveis.join(', ') : '—'}
          </p>
        </div>
        <div className="template-acoes">
          {template.editavel ? (
            <>
              <button className="btn btn-outline" onClick={() => handleEditarTemplate(template)}>
                Editar
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleExcluirTemplate(template)}
                disabled={excluindoId === template.id}
              >
                {excluindoId === template.id ? 'Excluindo...' : 'Excluir'}
              </button>
            </>
          ) : (
            <span className="template-readonly">Somente leitura</span>
          )}
        </div>
      </div>
    ));
  };

  const renderizarEditorTemplate = () => {
    if (!templateEditando) {
      return null;
    }
    return (
      <div className="editor-template">
        <h2>{templateEditando.novo ? 'Novo Template' : 'Editar Template'}</h2>

        <div className="form-grupo">
          <label htmlFor="nome">Nome do Template</label>
          <input
            type="text"
            id="nome"
            value={templateEditando.nome}
            onChange={(e) => setTemplateEditando((prev) => ({ ...prev, nome: e.target.value }))}
          />
        </div>

        {tipoApi === 'email' && (
          <div className="form-grupo">
            <label htmlFor="assunto">Assunto</label>
            <input
              type="text"
              id="assunto"
              value={templateEditando.assunto || ''}
              onChange={(e) => setTemplateEditando((prev) => ({ ...prev, assunto: e.target.value }))}
            />
          </div>
        )}

        <div className="form-grupo">
          <label htmlFor="conteudo">Conteúdo</label>
          <textarea
            id="conteudo"
            value={templateEditando.conteudo}
            onChange={(e) => setTemplateEditando((prev) => ({ ...prev, conteudo: e.target.value }))}
            rows={10}
          />
          <p className="form-ajuda">Use variáveis no formato <code>{'{NOME_VARIAVEL}'}</code> (ex.: {'{CLIENTE_NOME}'}).</p>
        </div>

        {variaveisDisponiveis.length > 0 && (
          <div className="form-grupo">
            <label>Variáveis disponíveis (clique para inserir)</label>
            <div className="variaveis-disponiveis">
              {variaveisDisponiveis.map((v) => (
                <button
                  key={v.nome}
                  type="button"
                  className="chip-variavel"
                  title={`${v.descricao} (ex.: ${v.exemplo})`}
                  onClick={() => inserirVariavel(v.nome)}
                >
                  {'{' + v.nome + '}'}
                </button>
              ))}
            </div>
          </div>
        )}

        {!templateEditando.novo && (
          <div className="form-grupo preview-bloco">
            <div className="preview-cabecalho">
              <label>Pré-visualização</label>
              <button type="button" className="btn btn-outline" onClick={handlePreview} disabled={previewCarregando}>
                {previewCarregando ? 'Gerando...' : 'Pré-visualizar'}
              </button>
            </div>
            {extrairPlaceholders(templateEditando).map((nome) => (
              <div className="preview-var" key={nome}>
                <label htmlFor={`pv-${nome}`}>{nome}</label>
                <input
                  type="text"
                  id={`pv-${nome}`}
                  value={previewVars[nome] || ''}
                  onChange={(e) => setPreviewVars((prev) => ({ ...prev, [nome]: e.target.value }))}
                />
              </div>
            ))}
            {previewResultado && (
              <div className="preview-resultado">
                {tipoApi === 'email' && previewResultado.assunto != null && (
                  <p><strong>Assunto:</strong> {previewResultado.assunto}</p>
                )}
                <pre className="preview-conteudo">{previewResultado.conteudo}</pre>
                {previewResultado.variaveisNaoSubstituidas?.length > 0 && (
                  <p className="preview-pendentes">
                    Sem valor: {previewResultado.variaveisNaoSubstituidas.join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {erroEditor && <p className="editor-erro" role="alert">{erroEditor}</p>}

        <div className="editor-acoes">
          <button className="btn btn-secondary" onClick={handleCancelarEdicao} disabled={salvando}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={handleSalvarTemplate} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="configuracoes-container">
      <div className="configuracoes-header">
        <h1>Configurações</h1>
        <p className="descricao">Personalize seus modelos de documentos e comunicações</p>
      </div>

      <div className="configuracoes-content">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'documentos' ? 'active' : ''}`}
            onClick={() => trocarAba('documentos')}
          >
            Documentos
          </button>
          <button
            className={`tab ${activeTab === 'emails' ? 'active' : ''}`}
            onClick={() => trocarAba('emails')}
          >
            E-mails
          </button>
          <button
            className={`tab ${activeTab === 'mensagens' ? 'active' : ''}`}
            onClick={() => trocarAba('mensagens')}
          >
            Mensagens
          </button>
          <button
            className={`tab ${activeTab === 'seguranca' ? 'active' : ''}`}
            onClick={() => trocarAba('seguranca')}
          >
            Segurança
          </button>
        </div>

        {activeTab === 'seguranca' ? (
          <div className="templates-container">
            <SegurancaSessoes />
          </div>
        ) : (
          <div className="templates-container">
            <div className="templates-header">
              <h2>
                {activeTab === 'documentos' && 'Templates de Documentos'}
                {activeTab === 'emails' && 'Templates de E-mails'}
                {activeTab === 'mensagens' && 'Templates de Mensagens'}
              </h2>
              {!modoEdicao && (
                <button className="btn btn-primary" onClick={handleAdicionarTemplate}>
                  Adicionar Template
                </button>
              )}
            </div>

            <div className="templates-content">
              {!modoEdicao && erroLista && (
                <p className="templates-erro-acao" role="alert">{erroLista}</p>
              )}
              {modoEdicao ? renderizarEditorTemplate() : (
                <div className="templates-lista">{renderizarListaTemplates()}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Configuracoes;
